import { useContent, useExternalTransition } from '@wix/funnel-builder-lizard'
import { forwardFunnelQueryParams, tryCatch } from '@wix/funnel-utils'
import { useEffect, useState } from 'react'

import { createAccountInvites } from '../api/AccountInvitesService/createAccountInvites'
import { Invite } from '../api/AccountInvitesService/types'
import { saveAgencyName } from '../api/AccountService'
import { saveRecentIntroData } from '../api/FunnelIntroHydraService/saveRecentIntroData'
import { parsePageBiSlug, useFlowLogger } from '../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../category'
import { useStorageContext } from '../context/StorageContext/StorageContext'
import { fedopsLogger } from '../monitoring/fedops'

type Status = 'pending' | 'success'
type State = {
  savingRecentIntroData?: Status
  creatingAccountInvites?: Status
  savingAgencyName?: Status
}

export const useFinishFlowV2 = () => {
  const externalTransitionUrl = useExternalTransition()
  const [statuses, setStatuses] = useState<State>()
  const { biSlug } = useContent()
  const flowLogger = useFlowLogger()
  const { appDefIds, category, businessName, logo, accountInvites, isWixStudioAccount, agencyName, isDeveloper } =
    useStorageContext()

  useEffect(() => {
    if (!externalTransitionUrl) {
      return
    }

    const makeRequests = async (
      accountInvites?: Invite[],
      businessName?: string,
      agencyName?: string,
      isWixStudioAccount?: boolean,
    ) => {
      if (accountInvites) {
        setStatuses(prevState => ({ ...prevState, creatingAccountInvites: 'pending' }))
        await tryCatch(() => createAccountInvites(accountInvites))
      }
      setStatuses({
        creatingAccountInvites: 'success',
        savingAgencyName: 'pending',
        savingRecentIntroData: 'pending',
      })
      await Promise.all([
        tryCatch(() =>
          saveRecentIntroData({
            category: introDataCategory,
            selectedApps,
            businessName,
            logo,
            industryId,
            structureId,
            isDeveloper,
          }),
        ),
        agencyName && tryCatch(() => saveAgencyName(agencyName)),
      ])
      setStatuses(prevState => ({
        ...prevState,
        savingAgencyName: 'success',
        savingRecentIntroData: 'success',
      }))

      // Undocumented API that allows await-ing for fedops events to be sent
      // fedopsLogger also has method .flush() that calls this, but doesn't return result and doesn't await it
      // https://github.com/wix-private/fedops/blob/e5b1b1cf6e4910767a0b61bb4420da63151bbd28/packages/fedops-logger/src/reporter/reporter.js#L20
      const reporter = fedopsLogger.getReporter() as any
      await tryCatch(() => reporter?._publisher?.flush())

      window.location.href = targetUrl
    }

    const targetUrl = forwardFunnelQueryParams(externalTransitionUrl)

    const [screenName, screenVersion] = parsePageBiSlug(biSlug)
    void flowLogger.finishFlow({ targetUrl, screenName, screenVersion })

    const businessTerm = category?.businessTerm
    const introDataCategory = resolveCategoryFromBusinessTerm(businessTerm, category?.businessSegmentId)
    const { industryId, structureId } = introDataCategory
    const selectedApps = appDefIds?.map(appDefId => ({ appDefId })) ?? []

    void makeRequests(accountInvites, businessName, agencyName, isWixStudioAccount)
  }, [
    isDeveloper,
    accountInvites,
    agencyName,
    appDefIds,
    biSlug,
    businessName,
    logo,
    category?.businessTerm,
    flowLogger,
    isWixStudioAccount,
    externalTransitionUrl,
  ])

  return statuses
}
